import { useState } from "react";
import { ORDERS_API } from "../../../../Utilities/APIs/APIs";
import useAxiosInstance from "../../../../Utilities/Hooks/useAxiosInstance";

function CancelOrderItem({
  setShowCancelOrderItem,
  orderID,
  targetID,
  triggerFetch,

  // new optional props (we’ll pass these from parent):
  orderStatus,
  orderItem,
}) {
  const axiosInstance = useAxiosInstance();
  const [cancelQty, setCancelQty] = useState(1);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // If parent hasn’t passed the line data yet, show a safe fallback
  if (!orderItem) {
    return (
      <div className="delete_item">
        <div className="delete_content">
          <h1>Cancel Order Item</h1>
          <p>Unable to load this line. Please reopen or refresh.</p>
          <div className="delete_btn">
            <button
              className="cancel"
              onClick={() => setShowCancelOrderItem(false)}
            >
              CLOSE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- quantities ----------------------------------------------------
  const qty = Number(orderItem.quantity || 0);

  // support both old `isCancelled` and new `cancelQuantity`
  let alreadyCancelled = Number(orderItem.cancelQuantity || 0);
  if (!alreadyCancelled && orderItem.isCancelled) {
    alreadyCancelled = qty;
  }

  const remainingCancelable = Math.max(0, qty - alreadyCancelled);

  // --- when do we need barcodes? -------------------------------------
  const isShippedOrDelivered = [
    "dispatchedInHouse",
    "dispatchedCourier",
    "delivered",
    "deliveredUnpaid",
  ].includes(orderStatus);

  const hasScans =
    Array.isArray(orderItem.scannedBarcodes) &&
    orderItem.scannedBarcodes.length > 0;

  const needsBarcodes = isShippedOrDelivered && hasScans;

  async function handleCancel(e) {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    const qtyNumber = Number(cancelQty || 0);

    if (!qtyNumber || qtyNumber <= 0) {
      setErrorMsg("Please enter a valid cancel quantity (> 0).");
      return;
    }

    if (qtyNumber > remainingCancelable) {
      setErrorMsg(
        `You can cancel at most ${remainingCancelable} unit(s) for this item.`
      );
      return;
    }

    let barcodes = [];

    if (needsBarcodes) {
      // split by newline or comma
      barcodes = barcodeInput
        .split(/\r?\n|,/)
        .map((s) => s.trim())
        .filter(Boolean);

      if (barcodes.length !== qtyNumber) {
        setErrorMsg(
          `Please provide exactly ${qtyNumber} barcode(s), one per line.`
        );
        return;
      }

      const known = orderItem.scannedBarcodes || [];
      const invalid = barcodes.filter((code) => !known.includes(code));

      if (invalid.length > 0) {
        setErrorMsg(
          `These barcodes are not associated with this item: ${invalid.join(
            ", "
          )}`
        );
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        cancelQuantity: qtyNumber,
      };
      if (needsBarcodes) {
        payload.barcodes = barcodes;
      }

      await axiosInstance.patch(
        `${ORDERS_API}cancelSingleOrderItem/${orderID}/${targetID}`,
        payload
      );

      setSuccessMsg("Item cancelled successfully.");
      triggerFetch();

      setTimeout(() => {
        setShowCancelOrderItem(false);
      }, 700);
    } catch (err) {
      console.error("cancelSingleOrderItem error", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to cancel item.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="">
      <div className="">

        {/* summary */}
        <div style={{ fontSize: 12, marginBottom: 10, lineHeight: 1.5, color: "#fff" }}>
          <div>
            <strong>Product:</strong> {orderItem?.item?.name}
          </div>
          <div>
            <strong>Size:</strong> {orderItem?.size?.name}
          </div>
          <div>
            <strong>Ordered qty:</strong> {qty}
          </div>
          <div>
            <strong>Already cancelled:</strong> {alreadyCancelled}
          </div>
          <div>
            <strong>Remaining cancellable:</strong> {remainingCancelable}
          </div>
          <div>
            <strong>Order status:</strong> {orderStatus}
          </div>
        </div>

        {remainingCancelable <= 0 ? (
          <>
            <p style={{ fontSize: 13, color: "#ffb74d", marginBottom: 8 }}>
              All quantity for this item is already cancelled.
            </p>
            <div className="delete_btn">
              <button
                className="cancel"
                onClick={() => setShowCancelOrderItem(false)}
              >
                CLOSE
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleCancel}>
            {/* cancel qty */}
            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 4,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#9ca3af",
                }}
              >
                Cancel quantity
              </label>
              <input
                type="number"
                min={1}
                max={remainingCancelable}
                value={cancelQty}
                onChange={(e) => setCancelQty(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 4,
                  border: "1px solid #555",
                  background: "#111827",
                  color: "#fff",
                  fontSize: 13,
                }}
              />
            </div>

            {/* barcodes only when needed */}
            {needsBarcodes && (
              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 4,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#9ca3af",
                  }}
                >
                  Returned barcode(s)
                  <span style={{ fontWeight: 400 }}>
                    {" "}
                    (one per line, must match scanned barcodes)
                  </span>
                </label>
                <textarea
                  rows={3}
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  placeholder={
                    orderItem.scannedBarcodes?.length
                      ? `Known: ${orderItem.scannedBarcodes.join(", ")}`
                      : ""
                  }
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: 4,
                    border: "1px solid #555",
                    background: "#111827",
                    color: "#fff",
                    fontSize: 13,
                  }}
                />
                <p
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: "#9ca3af",
                  }}
                >
                  Because this item was scanned/shipped, we need the returned
                  barcode(s) to put stock back correctly.
                </p>
              </div>
            )}

            {errorMsg && (
              <div
                style={{
                  marginTop: 6,
                  marginBottom: 6,
                  padding: "6px 8px",
                  borderRadius: 4,
                  background: "#991b1b",
                  color: "#fee2e2",
                  fontSize: 13,
                }}
              >
                {errorMsg}
              </div>
            )}

            {successMsg && (
              <div
                style={{
                  marginTop: 6,
                  marginBottom: 6,
                  padding: "6px 8px",
                  borderRadius: 4,
                  background: "#065f46",
                  color: "#d1fae5",
                  fontSize: 13,
                }}
              >
                {successMsg}
              </div>
            )}

            <div className="delete_btn">
              <button
                type="submit"
                className="delete"
                disabled={loading || remainingCancelable <= 0}
              >
                {loading ? "Processing..." : "Confirm cancel"}
              </button>
              <button
                type="button"
                className="cancel"
                onClick={() => setShowCancelOrderItem(false)}
              >
                Close
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CancelOrderItem;
